/**
 * "Lire la suite" sur mobile / tablette
 *
 * Deux points d'entrée côté éditeur Gutenberg pour rendre du contenu repliable :
 *
 *  1. Le bloc natif "Lire la suite" (Core/More) → insère un <!--more--> dans le HTML.
 *     Limité à UN par article par Gutenberg. Pratique pour les pages avec une coupure
 *     unique.
 *
 *  2. Un bloc "Groupe" (Core/Group) avec la classe CSS additionnelle `is-readmore`.
 *     Aussi simple à poser, et autant de fois que tu veux dans la page → permet
 *     plusieurs "Lire la suite" indépendants. Le contenu DU groupe sera replié,
 *     le bouton se pose juste avant.
 *
 * Le repli n'est actif qu'en dessous du breakpoint desktop (cf. read-more.scss).
 */

const TOGGLE_CLASS = 'read-more-toggle'
const COLLAPSE_CLASS = 'read-more-collapsed'
const OPEN_CLASS = 'is-open'
const PROCESSED_FLAG = '_readMoreProcessed'
const LABEL_OPEN = 'Lire la suite'
const LABEL_CLOSE = 'Voir moins'
const GROUP_SELECTOR = '.is-readmore'

let autoId = 0

function ensureId(el) {
  if (!el.id) {
    autoId += 1
    el.id = `read-more-${autoId}`
  }
  return el.id
}

function makeToggleButton(target) {
  const button = document.createElement('button')
  button.type = 'button'
  button.className = `${TOGGLE_CLASS} btn btn-link p-0`
  button.textContent = LABEL_OPEN
  button.setAttribute('aria-expanded', 'false')
  button.setAttribute('aria-controls', ensureId(target))

  button.addEventListener('click', () => {
    const isOpen = target.classList.toggle(OPEN_CLASS)
    button.setAttribute('aria-expanded', String(isOpen))
    button.textContent = isOpen ? LABEL_CLOSE : LABEL_OPEN
  })

  return button
}

function collectMoreComments(root) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT, {
    acceptNode(node) {
      return /^more(\s|$)/.test(node.nodeValue.trim())
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT
    }
  })

  const comments = []
  let current = walker.nextNode()
  while (current) {
    comments.push(current)
    current = walker.nextNode()
  }
  return comments
}

/**
 * Bloc Core/More : on extrait tout ce qui suit le commentaire <!--more-->
 * jusqu'au prochain marqueur (autre <!--more--> ou fin du parent), et on
 * l'enroule dans un wrapper repliable précédé d'un bouton.
 */
function setupCommentReadMore(comment) {
  const parent = comment.parentNode
  if (!parent || comment[PROCESSED_FLAG]) {
    return
  }

  const wrapper = document.createElement('div')
  wrapper.className = COLLAPSE_CLASS

  let next = comment.nextSibling
  while (next) {
    // Si on retombe sur un autre <!--more-->, on s'arrête : la prochaine itération
    // de la boucle d'init s'en occupera et créera son propre wrapper.
    if (next.nodeType === Node.COMMENT_NODE && /^more(\s|$)/.test(next.nodeValue.trim())) {
      break
    }

    const toMove = next
    next = next.nextSibling
    wrapper.appendChild(toMove)
  }

  if (!wrapper.childNodes.length) {
    return
  }

  const button = makeToggleButton(wrapper)
  parent.insertBefore(button, comment.nextSibling)
  parent.insertBefore(wrapper, button.nextSibling)

  comment[PROCESSED_FLAG] = true
}

/**
 * Bloc Core/Group + classe `is-readmore` : le groupe agit comme un marqueur.
 * On replie le groupe lui-même + tous ses siblings suivants jusqu'au prochain
 * marqueur (autre `.is-readmore` ou commentaire <!--more-->) ou la fin du parent.
 */
function isReadMoreMarker(node) {
  if (!node) {
    return false
  }
  if (node.nodeType === Node.ELEMENT_NODE && node.classList.contains('is-readmore')) {
    return true
  }
  if (node.nodeType === Node.COMMENT_NODE && /^more(\s|$)/.test(node.nodeValue.trim())) {
    return true
  }
  return false
}

function setupGroupReadMore(el) {
  if (el[PROCESSED_FLAG] || !el.parentNode) {
    return
  }

  const parent = el.parentNode
  const wrapper = document.createElement('div')
  wrapper.className = COLLAPSE_CLASS

  // Insère le wrapper juste avant le marqueur, puis y déplace le marqueur
  // et tous ses siblings suivants jusqu'au prochain marqueur (exclu).
  parent.insertBefore(wrapper, el)
  let current = el
  while (current) {
    const isNextMarker = current !== el && isReadMoreMarker(current)
    if (isNextMarker) {
      break
    }
    const toMove = current
    current = current.nextSibling
    wrapper.appendChild(toMove)
  }

  const button = makeToggleButton(wrapper)
  parent.insertBefore(button, wrapper)

  el[PROCESSED_FLAG] = true
}

function init() {
  // On collecte d'abord tous les marqueurs, puis on les traite : ainsi chaque
  // marqueur encore "en place" dans le parent crée bien sa propre section,
  // sans se faire avaler par le marqueur précédent.
  collectMoreComments(document.body).forEach(setupCommentReadMore)
  Array.from(document.querySelectorAll(GROUP_SELECTOR)).forEach(setupGroupReadMore)
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init)
} else {
  init()
}
